import {
  Flex,
  Text,
  Input,
  Box,
  InputGroup,
  Button,
  Stack,
  HStack,
  Switch,
  InputLeftAddon,
  Spacer,
  Tooltip
} from '@chakra-ui/react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { isEmail, isURL } from 'validator';

import { CloseIcon } from '@assets/icons';
import { getRuleTypeLabel } from '@util/helpers';
import StackRadio from '@components/general/form/radio.component';

const addOnProps = {
  h: 'inherit',
  bgColor: 'surface.containerHigh',
  border: 'reskin',
  color: 'surface.onSurfaceVariant',
  borderTopLeftRadius: 'lg',
  borderBottomLeftRadius: 'lg'
};

export default function FocusRuleForm({ form }) {
  const { control, formState, register } = form;
  const titleFieldArray = useFieldArray({ control, name: 'titles' });
  const domainFieldArray = useFieldArray({ control, name: 'domains' });
  const onlyDomainFieldArray = useFieldArray({ control, name: 'only_domains' });
  const notDomainFieldArray = useFieldArray({ control, name: 'not_domains' });
  const neverDomainFieldArray = useFieldArray({ control, name: 'never_domains' });
  const emailFieldArray = useFieldArray({ control, name: 'emails' });

  const { automateBy } = useWatch({ control });
  const automationOptions = [
    { value: 'titles', label: 'Meeting title' },
    { value: 'domains', label: 'Organization Is' },
    { value: 'only_domains', label: 'Organization Can Only Be' },
    { value: 'not_domains', label: 'Organization Is Not' },
    { value: 'never_domains', label: 'Organization Can Never Be' },
    { value: 'emails', label: 'Meeting Attendee Email Is' },
    { value: 'organizers', label: 'Meeting Organizer Email Is' }
  ];

  const lockRequiredOption = automateBy === 'never_domains' || automateBy === 'only_domains';

  return (
    <form id='add-rules-form'>
      <Flex
        flexDir='column'
        gap={8}
        mt={2}
        py={2}
      >
        <Tooltip
          label="'Organization Can Never Be'/'Organization Can Only Be' Rules Are Always Required"
          isDisabled={!lockRequiredOption}
        >
          <Stack
            gap={3}
            opacity={lockRequiredOption ? 0.3 : 1}
            cursor={lockRequiredOption ? 'pointer' : 'initial'}
          >
            <Text fontWeight={600}>Set as Required</Text>
            <Switch
              display='flex'
              alignItems='center'
              fontSize='sm'
              {...register('isRequired')}
            >
              This rule must be met for this category to be assigned to a meeting
            </Switch>
          </Stack>
        </Tooltip>
        <Stack gap={3}>
          <Text fontWeight={600}>Automate By</Text>
          <StackRadio
            name='automateBy'
            control={control}
            options={automationOptions}
          />
        </Stack>
        {automateBy === 'titles' && (
          <FieldWrapper
            label='Meeting title'
            buttonLabel='keyword'
            fields={titleFieldArray.fields}
            onAddClick={() => titleFieldArray.append({ value: '' })}
            onRemoveClick={(index) => titleFieldArray.remove(index)}
          >
            {(_field, index) => (
              <Input
                placeholder='Enter meeting title keyword'
                {...register(`titles.${index}.value`, {
                  required: 'Must Not Be Empty'
                })}
              />
            )}
          </FieldWrapper>
        )}
        {automateBy === 'domains' && (
          <FieldWrapper
            label='Organization'
            buttonLabel='organization'
            fields={domainFieldArray.fields}
            onAddClick={() => domainFieldArray.append({ invitees: 1, value: '' })}
            onRemoveClick={(index) => domainFieldArray.remove(index)}
          >
            {(_field, index) => (
              <>
                <Text>Meeting has at least</Text>
                <Input
                  w={12}
                  type='number'
                  min={1}
                  {...register(`domains.${index}.invitees`, {
                    min: { value: 1, message: 'Must Have At Least 1 Invitee' }
                  })}
                />
                <Text>invitees from</Text>
                <InputGroup w={64}>
                  <InputLeftAddon {...addOnProps}>@</InputLeftAddon>
                  <Input
                    placeholder='acmeinc.com'
                    {...register(`domains.${index}.value`, {
                      required: 'Must Not Be Empty',
                      validate: (v) => isURL(v) || 'Must be Valid Email Domain'
                    })}
                  />
                </InputGroup>
              </>
            )}
          </FieldWrapper>
        )}
        {automateBy === 'only_domains' && (
          <FieldWrapper
            label='Organization Can Only Be'
            buttonLabel='organization'
            descriptionText='Meeting only has attendees from'
            fields={onlyDomainFieldArray.fields}
            onAddClick={() => onlyDomainFieldArray.append({ value: '' })}
            onRemoveClick={(index) => onlyDomainFieldArray.remove(index)}
          >
            {(_field, index) => (
              <InputGroup w={64}>
                <InputLeftAddon {...addOnProps}>@</InputLeftAddon>
                <Input
                  placeholder='acmeinc.com'
                  {...register(`only_domains.${index}.value`, {
                    required: 'Must Not Be Empty',
                    validate: (v) => isURL(v) || 'Must be Valid Email Domain'
                  })}
                />
              </InputGroup>
            )}
          </FieldWrapper>
        )}
        {automateBy === 'not_domains' && (
          <FieldWrapper
            label='Organization is Not'
            buttonLabel='organization'
            descriptionText='Meeting has at least'
            fields={notDomainFieldArray.fields}
            onAddClick={() => notDomainFieldArray.append({ invitees: 1, value: '' })}
            onRemoveClick={(index) => notDomainFieldArray.remove(index)}
          >
            {(_field, index) => (
              <>
                <Input
                  w={12}
                  type='number'
                  min={1}
                  {...register(`not_domains.${index}.invitees`, {
                    min: { value: 1, message: 'Must Have At Least 1 Invitee' }
                  })}
                />
                <Text>
                  invitees that are <b>not</b> from
                </Text>
                <InputGroup w={64}>
                  <InputLeftAddon {...addOnProps}>@</InputLeftAddon>
                  <Input
                    placeholder='acmeinc.com'
                    {...register(`not_domains.${index}.value`, {
                      required: 'Must Not Be Empty',
                      validate: (v) => isURL(v) || 'Must be Valid Email Domain'
                    })}
                  />
                </InputGroup>
              </>
            )}
          </FieldWrapper>
        )}
        {automateBy === 'never_domains' && (
          <FieldWrapper
            label='Organization Can Never Be'
            buttonLabel='organization'
            fields={neverDomainFieldArray.fields}
            onAddClick={() => neverDomainFieldArray.append({ value: '' })}
            onRemoveClick={(index) => neverDomainFieldArray.remove(index)}
          >
            {(_field, index) => (
              <>
                <Text>Meeting can not have any invitees from</Text>
                <InputGroup w={64}>
                  <InputLeftAddon {...addOnProps}>@</InputLeftAddon>
                  <Input
                    placeholder='acmeinc.com'
                    {...register(`never_domains.${index}.value`, {
                      required: 'Must Not Be Empty',
                      validate: (v) => isURL(v) || 'Must be Valid Email Domain'
                    })}
                  />
                </InputGroup>
              </>
            )}
          </FieldWrapper>
        )}
        {automateBy === 'emails' && (
          <FieldWrapper
            label='Meeting Attendee Email Is'
            buttonLabel='attendee'
            fields={emailFieldArray.fields}
            onAddClick={() => emailFieldArray.append({ value: '' })}
            onRemoveClick={(index) => emailFieldArray.remove(index)}
          >
            {(_field, index) => (
              <Input
                type='email'
                placeholder='name@email.com'
                {...register(`emails.${index}.value`, {
                  required: 'Must Not Be Empty',
                  validate: (v) => isEmail(v) || 'Must be Valid Email'
                })}
              />
            )}
          </FieldWrapper>
        )}
        {automateBy === 'organizers' && (
          <FieldWrapper
            label='Meeting Organizer Email Is'
            buttonLabel='organizer'
            fields={emailFieldArray.fields}
            onAddClick={() => emailFieldArray.append({ value: '' })}
            onRemoveClick={(index) => emailFieldArray.remove(index)}
          >
            {(_field, index) => (
              <Input
                type='email'
                placeholder='name@email.com'
                {...register(`organizers.${index}.value`, {
                  required: 'Must Not Be Empty',
                  validate: (v) => isEmail(v) || 'Must be Valid Email'
                })}
              />
            )}
          </FieldWrapper>
        )}
        {formState.errors[automateBy] ? (
          <Box color='critical.primary'>
            {formState.errors[automateBy].map((error) => {
              const errValue = error?.value?.ref?.value;
              const errMsg = error?.value?.message ?? error?.invitees?.message;
              return (
                <Box key={`rule-form-${errMsg}`}>{`${
                  errValue ? `"${errValue}"` : `"${getRuleTypeLabel(automateBy)}" Values `
                } ${errMsg}`}</Box>
              );
            })}
          </Box>
        ) : null}
      </Flex>
    </form>
  );
}

function FieldWrapper({
  label,
  descriptionText,
  buttonLabel,
  fields,
  onAddClick,
  onRemoveClick,
  children
}) {
  return (
    <Stack gap={3}>
      <Text fontWeight={600}>{label}</Text>
      {descriptionText && <Text fontSize='sm'>{descriptionText}</Text>}
      {fields.map((field, index) => (
        <HStack
          gap={2}
          fontSize='sm'
          key={field?.id}
        >
          {children(field, index)}
          <Spacer />
          <Button
            variant='reskinGhost'
            colorScheme='critical'
            size='reskinXs'
            px={2}
            iconSpacing={1}
            leftIcon={<CloseIcon />}
            onClick={() => onRemoveClick(index)}
          >
            Remove
          </Button>
        </HStack>
      ))}
      <Box>
        <Button
          size='reskinSm'
          variant='reskinLink'
          onClick={onAddClick}
        >
          Add another {buttonLabel}
        </Button>
      </Box>
    </Stack>
  );
}
